import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Moon, Sun } from 'lucide-react';
import logo from '../assets/SP3Dlogonewblue.png';
import { theme } from '../styles/theme';
import { extractApiError } from '../utils/errorMessages';

const LoginTemplate: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  // Aliased: the imported `theme` (styles/theme) is the class-token object,
  // while useTheme() returns the current mode as a string. Destructuring it
  // as `theme` shadowed the import, so every `${theme.text.primary}` in this
  // file resolved against a string. It went unnoticed because those class
  // expressions were plain double-quoted strings and never interpolated at
  // all; repairing them to template literals is what surfaced it.
  const { theme: mode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login({ username: email, password });
      navigate('/dashboard');
    } catch (err: unknown) {
      setError(extractApiError(err, 'Login failed. Check your username and password.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-subtle transition-colors duration-300">
      {/* Logo in top left corner - Fixed position */}
      <div className="fixed top-6 left-6 z-50">
        <img 
          src={logo} 
          alt="SpareParts3D Logo" 
          className="h-12 w-auto transition-opacity duration-300"
        />
      </div>

      {/* Theme Toggle in top right corner - Fixed position */}
      <button
        onClick={toggleTheme}
        className="fixed top-6 right-6 z-50 w-20 h-10 rounded-full bg-bg-subtle shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-border overflow-hidden"
        aria-label="Toggle theme"
        style={{ position: 'fixed', top: '1.5rem', right: '1.5rem' }}
      >
        {/* Sun Icon (Left side) */}
        <div className="absolute left-2 top-1/2 -translate-y-1/2">
          <Sun className="w-5 h-5 text-qc-warning" />
        </div>
        
        {/* Moon Icon (Right side) */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <Moon className="w-5 h-5 text-text-tertiary" />
        </div>
        
        {/* Sliding Circle */}
        <div
          className={`absolute top-1 w-7 h-7 bg-bg-surface rounded-full shadow-md transition-all duration-300 ease-in-out ${
            mode === 'light' ? 'left-1' : 'left-[calc(100%-2rem)]'
          }`}
        />
      </button>

      {/* Center content */}
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
        {/* Card Container */}
        <div className={`${theme.card.background} rounded-2xl shadow-xl p-8 transition-colors duration-300`}>
          {/* Logo/Title Section */}
          <div className="text-center mb-8">
            <h1 className={`text-3xl font-bold ${theme.text.primary} mb-2`}>
              Welcome Back
            </h1>
            <p className={`${theme.text.secondary} text-sm`}>
              Sign in to continue to your account
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-qc-error/10 border border-qc-error/30 text-qc-error px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username Input */}
            <div>
              <label 
                htmlFor="email" 
                className={`block text-sm font-medium ${theme.text.secondary} mb-2`}
              >
                Username or Email
              </label>
              <input
                id="email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className={`w-full px-4 py-3 border border-border rounded-lg bg-bg-surface ${theme.text.primary} placeholder-text-tertiary focus:ring-2 focus:ring-interactive-primary focus:border-transparent transition duration-200 disabled:bg-bg-subtle disabled:cursor-not-allowed`}
                placeholder="Enter your username or email"
              />
            </div>

            {/* Password Input */}
            <div>
              <label 
                htmlFor="password" 
                className={`block text-sm font-medium ${theme.text.secondary} mb-2`}
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className={`w-full px-4 py-3 border border-border rounded-lg bg-bg-surface ${theme.text.primary} placeholder-text-tertiary focus:ring-2 focus:ring-interactive-primary focus:border-transparent transition duration-200 disabled:bg-bg-subtle disabled:cursor-not-allowed`}
                placeholder="Enter your password"
              />
            </div>

            {/* Forgot Password Link */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember"
                  type="checkbox"
                  className={`h-4 w-4 text-interactive-primary focus:ring-interactive-primary ${theme.border.secondary} rounded bg-bg-surface`}
                />
                <label htmlFor="remember" className={`ml-2 block text-sm ${theme.text.secondary}`}>
                  Remember me
                </label>
              </div>
              <button
                type="button"
                className="text-sm text-interactive-primary hover:text-interactive-primary-hover font-medium"
              >
                Forgot password?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:bg-interactive-primary disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-interactive-primary focus:ring-offset-2"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-text-tertiary">
          Internal tool - Contact your administrator for access
        </p>
        </div>
      </div>
    </div>
  );
};

export default LoginTemplate;
